/**
 * /pending-approval — ver `MEMBERSHIP_ROUTE.PENDING_APPROVAL`
 *
 * Espera de aprobación del equipo (membresía). Polling a Clerk + sync membresía.
 * Cuando `approved` → redirección a `MEMBERSHIP_ROUTE.MI_ACCESO`.
 *
 * UX: mobile / PWA — full-viewport shell, safe-area, sticky bottom actions,
 * targets ≥48px, feedback de press, status live sin ruido.
 */

import { MembershipPortalHint } from "@/components/membership/MembershipPortalHint";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useSEO } from "@/hooks/useSEO";
import { getUserDisplayName, useClerkSessionClaims } from "@/lib/auth";
import {
  BRAND_PERSON_NAME,
  BRAND_SITE_URL,
  BRAND_SUPPORT_EMAIL,
} from "@/lib/brandConfig";
import { CANONICAL_VIP_PRICE_USD } from "@/lib/canonicalVipMembership";
import { detectCountryCode } from "@/lib/countryDetection";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import { resolveCheckoutCountryCode } from "@/lib/resolveCheckoutCountryCode";
import {
  localeForPlansFromMuxLanguage,
  startVipMembershipVideoCheckout,
} from "@/lib/vipMembershipVideoCheckout";
import { cn } from "@/lib/utils";
import { useClerk, useSession, useUser } from "@clerk/clerk-react";
import {
  Bell,
  CheckCircle2,
  Clock,
  Loader2,
  LogOut,
  Mail,
  MessageCircle,
  Sparkles,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";

const POLL_INTERVAL_MS = 12_000;
const MEMBERSHIP_SYNC_MIN_MS = 60_000;
const NOTIFY_COOLDOWN_MS = 30 * 60 * 1000;
const STORAGE_KEY_PREFIX = "pending_approval_notify_";

const SEO = {
  title: "Estamos preparando tu acceso",
  description: `En un rato te mandamos el link de ingreso a la comunidad de ${BRAND_PERSON_NAME}.`,
  keywords: "",
  ogImage: `${BRAND_SITE_URL}/og.png`,
  canonical: `${BRAND_SITE_URL}${MEMBERSHIP_ROUTE.PENDING_APPROVAL}`,
  robots: "noindex, nofollow" as const,
};

/** Shared PWA shell: safe areas, full viewport, no horizontal scroll. */
function PendingShell({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "fixed inset-0 z-[100] flex flex-col overflow-hidden bg-zinc-950 text-zinc-100",
        "pt-[env(safe-area-inset-top,0px)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function PressableButton({
  className,
  children,
  ...props
}: ComponentProps<typeof Button>) {
  return (
    <Button
      {...props}
      className={cn(
        "min-h-12 touch-manipulation select-none rounded-2xl text-[15px] font-semibold",
        "transition-[transform,background-color,border-color,opacity] duration-150 ease-out",
        "active:scale-[0.97] disabled:active:scale-100",
        className,
      )}
    >
      {children}
    </Button>
  );
}

function StatusPill({
  polling,
  lastChecked,
}: {
  polling: boolean;
  lastChecked: Date | null;
}) {
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5"
      role="status"
      aria-live="polite"
    >
      {polling ? (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin text-amber-300" aria-hidden />
      ) : (
        <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400/50" />
          <span className="relative inline-flex h-full w-full rounded-full bg-amber-400" />
        </span>
      )}
      <span className="text-[11px] font-medium tabular-nums text-zinc-400">
        {polling
          ? "Verificando…"
          : lastChecked
            ? `Actualizado ${lastChecked.toLocaleTimeString("es-AR", {
                hour: "2-digit",
                minute: "2-digit",
              })}`
            : "En vivo"}
      </span>
    </div>
  );
}

function StepList({ items }: { items: string[] }) {
  return (
    <ol className="space-y-2.5">
      {items.map((text, i) => (
        <li key={text} className="flex items-start gap-3">
          <span
            className={cn(
              "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tabular-nums",
              i === 0
                ? "bg-amber-400 text-zinc-950"
                : "border border-white/15 bg-white/5 text-zinc-400",
            )}
            aria-hidden
          >
            {i + 1}
          </span>
          <span className="pt-0.5 text-[13px] leading-snug text-zinc-300">{text}</span>
        </li>
      ))}
    </ol>
  );
}

export default function PendingApproval() {
  useSEO(SEO);
  const { user, isLoaded: userLoaded } = useUser();
  const { session, isLoaded: sessionLoaded } = useSession();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { claims } = useClerkSessionClaims();

  const email = user?.primaryEmailAddress?.emailAddress ?? "";
  const displayName = getUserDisplayName(user);
  const fromVip = searchParams.get("from_vip") === "1" || searchParams.get("from_special_offer") === "1";
  const planTierHint = (searchParams.get("plan_tier") || "").trim().toLowerCase();
  const fromBasicPurchase = searchParams.get("from_basic") === "1" || planTierHint === "basic";
  const fromStandardPurchase =
    searchParams.get("from_standard") === "1" || planTierHint === "standard";
  const isAccessRequest = searchParams.get("access_request") === "1";
  const fromFree = searchParams.get("from_free") === "true";
  const claimedPaid =
    searchParams.get("claimed_paid") === "1" ||
    searchParams.get("from_recovery") === "1";
  const offerMembershipParam = searchParams.get("offer_membership") === "1";
  const hasPaidSignal =
    fromVip ||
    fromBasicPurchase ||
    fromStandardPurchase ||
    claimedPaid ||
    planTierHint === "devil" ||
    Boolean(searchParams.get("session_id")) ||
    Boolean(searchParams.get("subscription_id")) ||
    ["vip", "basic", "standard", "platinum", "devil"].includes(planTierHint);
  const showBuyMembershipCta =
    !hasPaidSignal || fromFree || isAccessRequest || offerMembershipParam;
  const postCheckoutMessaging =
    !isAccessRequest &&
    !fromFree &&
    !showBuyMembershipCta &&
    (fromVip ||
      fromBasicPurchase ||
      fromStandardPurchase ||
      planTierHint === "devil" ||
      claimedPaid);

  const countryCode = useMemo(
    () => resolveCheckoutCountryCode(detectCountryCode()),
    [],
  );

  const [approved, setApproved] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [polling, setPolling] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [manualChecking, setManualChecking] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [hasNotified, setHasNotified] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const approvalHandledRef = useRef(false);
  const lastSyncAtRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const clearAll = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
  }, []);

  useEffect(() => {
    if (!email) return;
    const storageKey = `${STORAGE_KEY_PREFIX}${email}`;
    const tick = () => {
      const last = localStorage.getItem(storageKey);
      if (!last) {
        setCooldownRemaining(0);
        setHasNotified(false);
        return;
      }
      const ts = parseInt(last, 10);
      if (isNaN(ts)) {
        localStorage.removeItem(storageKey);
        setCooldownRemaining(0);
        setHasNotified(false);
        return;
      }
      const elapsed = Date.now() - ts;
      const remaining = NOTIFY_COOLDOWN_MS - elapsed;
      if (remaining <= 0) {
        localStorage.removeItem(storageKey);
        setCooldownRemaining(0);
        setHasNotified(false);
      } else {
        setCooldownRemaining(remaining);
        setHasNotified(true);
      }
    };
    tick();
    cooldownRef.current = setInterval(tick, 1000);
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [email]);

  const handleApproved = useCallback(() => {
    if (approvalHandledRef.current) return;
    approvalHandledRef.current = true;
    clearAll();
    if (!mountedRef.current) return;
    setApproved(true);
    let secs = 3;
    setCountdown(secs);
    countdownRef.current = setInterval(() => {
      secs -= 1;
      if (secs <= 0) {
        if (countdownRef.current) clearInterval(countdownRef.current);
        navigate(MEMBERSHIP_ROUTE.MI_ACCESO, { replace: true });
      } else if (mountedRef.current) {
        setCountdown(secs);
      }
    }, 1000);
  }, [clearAll, navigate]);

  const checkApproval = useCallback(
    async (isManual = false) => {
      if (!user || !mountedRef.current) return;
      if (isManual && mountedRef.current) setManualChecking(true);
      if (mountedRef.current) setPolling(true);
      try {
        if (session) {
          const now = Date.now();
          const shouldSync =
            isManual ||
            lastSyncAtRef.current === 0 ||
            now - lastSyncAtRef.current >= MEMBERSHIP_SYNC_MIN_MS;
          if (shouldSync) {
            const token = await session.getToken();
            if (token) {
              lastSyncAtRef.current = now;
              try {
                await fetch("/api/sync-my-membership", {
                  headers: { Authorization: `Bearer ${token}` },
                });
                await session.getToken({ skipCache: true });
              } catch {
                /* network hiccup */
              }
            }
          }
        }
        const refreshed = await user.reload();
        if (!mountedRef.current) return;
        setLastChecked(new Date());
        if (refreshed.publicMetadata?.approved === true) {
          handleApproved();
          return;
        }
        if (refreshed.publicMetadata?.rejected === true) {
          clearAll();
          setRejected(true);
          return;
        }
        if (refreshed.publicMetadata?.banned === true) {
          void signOut();
          return;
        }
      } catch {
        /* ignore */
      }
      if (mountedRef.current) {
        setPolling(false);
        if (isManual) setManualChecking(false);
      }
    },
    [user, session, handleApproved, clearAll, signOut],
  );

  useEffect(() => {
    if (!user?.id || !sessionLoaded) return;
    const alreadyApproved =
      claims.approved === true || user.publicMetadata?.approved === true;
    if (alreadyApproved) {
      handleApproved();
      return;
    }
    if (user.publicMetadata?.rejected === true) {
      setRejected(true);
      return;
    }
    void checkApproval();
    intervalRef.current = setInterval(() => {
      void checkApproval();
    }, POLL_INTERVAL_MS);
    return () => clearAll();
  }, [
    user?.id,
    sessionLoaded,
    user?.publicMetadata?.approved,
    claims.approved,
    checkApproval,
    handleApproved,
    clearAll,
  ]);

  const startMembershipCheckout = useCallback(async () => {
    if (checkoutBusy) return;
    setCheckoutBusy(true);
    try {
      const cancelQs = new URLSearchParams();
      cancelQs.set("offer_membership", "1");
      if (fromFree) cancelQs.set("from_free", "true");
      if (isAccessRequest) cancelQs.set("access_request", "1");
      await startVipMembershipVideoCheckout({
        countryCode: countryCode === "UNKNOWN" ? "US" : countryCode,
        pathname: location.pathname,
        languageForPlans: localeForPlansFromMuxLanguage("es"),
        email: email || undefined,
        template: "PendingApproval",
        template_type: "PendingApproval",
        cancelPath: `${MEMBERSHIP_ROUTE.PENDING_APPROVAL}?${cancelQs.toString()}`,
      });
    } catch {
      setCheckoutBusy(false);
      toast({
        title: "No se pudo abrir el checkout",
        description: "Reintentá en unos segundos o escribinos a soporte.",
        variant: "destructive",
      });
    }
  }, [
    checkoutBusy,
    countryCode,
    location.pathname,
    email,
    toast,
    fromFree,
    isAccessRequest,
  ]);

  const notifyAdmin = useCallback(async () => {
    if (!user || !email) return;
    const storageKey = `${STORAGE_KEY_PREFIX}${email}`;
    const last = localStorage.getItem(storageKey);
    if (last) {
      const ts = parseInt(last, 10);
      const remaining = NOTIFY_COOLDOWN_MS - (Date.now() - ts);
      if (remaining > 0) {
        const min = Math.ceil(remaining / 60000);
        toast({
          title: "Esperá un poco",
          description: `Ya avisaste al equipo. Volvé a intentar en ${min} minuto${min !== 1 ? "s" : ""}.`,
          variant: "destructive",
        });
        return;
      }
    }
    setNotifying(true);
    try {
      const res = await fetch("/api/admin/notify-pending-subscriber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          name: displayName,
          clerkUserId: user.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error === "banned" || data.banned) {
          toast({
            title: "Cuenta bloqueada",
            description: "Excediste el límite de notificaciones. Tu cuenta fue bloqueada por abuso.",
            variant: "destructive",
            duration: 8000,
          });
          setTimeout(() => signOut(), 3000);
          return;
        }
        if (data.error === "cooldown_active") {
          const min = Math.ceil((data.remaining_ms ?? NOTIFY_COOLDOWN_MS) / 60000);
          toast({
            title: "Esperá un poco",
            description: `Ya avisaste al equipo. Volvé a intentar en ${min} minuto${min !== 1 ? "s" : ""}.`,
            variant: "destructive",
          });
          return;
        }
        throw new Error(data.error || "Error al avisar al equipo");
      }
      localStorage.setItem(storageKey, Date.now().toString());
      setHasNotified(true);
      setCooldownRemaining(NOTIFY_COOLDOWN_MS);
      toast({
        title: "Aviso enviado",
        description: "El equipo recibió tu mensaje. Te mandamos el link apenas esté listo.",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "No se pudo enviar el aviso.",
        variant: "destructive",
      });
    } finally {
      setNotifying(false);
    }
  }, [user, email, displayName, toast, signOut]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (!userLoaded || !sessionLoaded) {
    return (
      <PendingShell className="items-center justify-center">
        <Loader2
          className="h-8 w-8 animate-spin text-white/35"
          aria-hidden
        />
        <span className="sr-only">Cargando…</span>
      </PendingShell>
    );
  }

  if (!user) {
    const claimedPaidNav =
      searchParams.get("claimed_paid") === "1" ||
      searchParams.get("from_recovery") === "1";
    const back = `${MEMBERSHIP_ROUTE.PENDING_APPROVAL}?claimed_paid=1&from_recovery=1`;
    if (claimedPaidNav) {
      return (
        <Navigate
          to={`${MEMBERSHIP_ROUTE.PORTAL_REGISTRO}?redirect_url=${encodeURIComponent(back)}`}
          replace
        />
      );
    }
    return <Navigate to={MEMBERSHIP_ROUTE.REGISTRO} replace />;
  }

  // ── Rejected ─────────────────────────────────────────────────────────────
  if (rejected) {
    return (
      <PendingShell>
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))]">
          <div className="w-full max-w-md space-y-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-red-500/15 ring-1 ring-red-500/25">
              <Mail className="h-8 w-8 text-red-300" aria-hidden />
            </div>
            <div className="space-y-2">
              <h1 className="text-xl font-semibold tracking-tight text-red-50">
                Solicitud no aprobada
              </h1>
              <p className="text-[15px] leading-relaxed text-red-200/70">
                Esta vez no pudimos darte acceso. Si creés que es un error,
                escribinos a{" "}
                <a
                  href={`mailto:${BRAND_SUPPORT_EMAIL}`}
                  className="font-medium text-red-100 underline underline-offset-2"
                >
                  {BRAND_SUPPORT_EMAIL}
                </a>
                .
              </p>
            </div>
            <PressableButton
              variant="outline"
              className="w-full border-red-500/35 bg-red-500/10 text-red-100 hover:bg-red-500/20"
              onClick={() => void signOut()}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Cerrar sesión
            </PressableButton>
          </div>
        </div>
      </PendingShell>
    );
  }

  // ── Approved transition ──────────────────────────────────────────────────
  if (approved) {
    return (
      <PendingShell>
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))]">
          <div className="w-full max-w-md space-y-5 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-emerald-500/15 ring-1 ring-emerald-500/30">
              <CheckCircle2 className="h-8 w-8 text-emerald-300" aria-hidden />
            </div>
            <div className="space-y-2">
              <h1 className="flex items-center justify-center gap-2 text-2xl font-semibold tracking-tight text-emerald-50">
                <Sparkles className="h-5 w-5 text-amber-300" aria-hidden />
                ¡Listo!
              </h1>
              <p className="text-[15px] leading-relaxed text-emerald-200/80">
                Tu acceso ya está. Te llevamos al link en un momento.
              </p>
            </div>
            <p className="text-xs tabular-nums text-emerald-200/55">
              Redirigiendo en {countdown}s…
            </p>
            <PressableButton
              className="w-full bg-emerald-400 font-bold text-zinc-950 hover:bg-emerald-300"
              onClick={() => navigate(MEMBERSHIP_ROUTE.MI_ACCESO, { replace: true })}
            >
              Ir ahora
            </PressableButton>
          </div>
        </div>
      </PendingShell>
    );
  }

  // ── Main waiting UI ──────────────────────────────────────────────────────
  const headline = fromFree
    ? "Registro listo"
    : displayName
      ? `Hola, ${displayName}`
      : "Hola";

  const subcopy = showBuyMembershipCta
    ? `Tu cuenta está lista. Activá la membresía VIP para entrar al grupo privado de ${BRAND_PERSON_NAME}.`
    : fromFree
      ? "Estamos preparando tu link de acceso. En un rato te llega por email."
      : postCheckoutMessaging
        ? "¡Gracias! Recibimos tu pago. Estamos preparando tu link de ingreso."
        : "Estamos preparando tu link de acceso. No tenés que hacer nada más.";

  const steps = showBuyMembershipCta
    ? [
        "Activás la membresía VIP (pago seguro)",
        "Confirmamos el pago y preparamos tu link",
        "Te mandamos el acceso por email",
      ]
    : [
        isAccessRequest
          ? "Revisamos tu solicitud"
          : fromFree
            ? "Confirmamos tu registro"
            : "Confirmamos tu cuenta y pago",
        "Preparamos tu link privado",
        "Te lo mandamos por email (esta pantalla se actualiza sola)",
      ];

  const notifyDisabled = notifying || (hasNotified && cooldownRemaining > 0);

  return (
    <PendingShell>
      {/* Scrollable body */}
      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto overscroll-y-contain",
          "[-webkit-overflow-scrolling:touch]",
        )}
      >
        <div className="mx-auto w-full max-w-md px-5 pb-4 pt-4 sm:pt-8">
          {/* Top bar */}
          <div className="mb-6 flex items-center justify-between gap-3">
            <p className="truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
              Acceso privado
            </p>
            <StatusPill polling={polling} lastChecked={lastChecked} />
          </div>

          {/* Hero */}
          <div className="mb-6 space-y-4 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-[1.25rem] bg-amber-400/15 ring-1 ring-amber-400/25">
              <Clock className="h-7 w-7 text-amber-300" aria-hidden />
            </div>
            <div className="space-y-2">
              <h1 className="text-balance text-[1.375rem] font-semibold leading-tight tracking-tight text-zinc-50 sm:text-2xl">
                {headline}
              </h1>
              <p className="text-pretty text-[15px] leading-relaxed text-zinc-400">
                {subcopy}
              </p>
            </div>
          </div>

          {fromFree && user?.unsafeMetadata?.telegram ? (
            <div className="mb-4 flex items-center gap-2.5 rounded-2xl border border-white/10 bg-white/[0.04] px-3.5 py-3">
              <MessageCircle className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
              <div className="min-w-0 text-left text-[13px]">
                <span className="text-zinc-500">Telegram · </span>
                <code className="break-all font-medium text-zinc-200">
                  {String(user.unsafeMetadata.telegram)}
                </code>
              </div>
            </div>
          ) : null}

          {showBuyMembershipCta ? (
            <div className="mb-4 space-y-3 rounded-3xl border border-amber-400/30 bg-gradient-to-b from-amber-400/15 to-amber-400/[0.04] p-4">
              <div className="space-y-1 text-center">
                <p className="text-sm font-semibold text-amber-50">Membresía VIP</p>
                <p className="text-[13px] leading-relaxed text-amber-100/75">
                  Desde{" "}
                  <span className="font-semibold text-amber-50">
                    US${CANONICAL_VIP_PRICE_USD.toFixed(2)}
                  </span>
                  /mes · acceso + grupo privado
                </p>
              </div>
              <PressableButton
                className="w-full bg-amber-400 font-bold text-zinc-950 hover:bg-amber-300"
                disabled={checkoutBusy}
                onClick={() => void startMembershipCheckout()}
              >
                {checkoutBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Abriendo Stripe…
                  </>
                ) : (
                  "Comprar membresía VIP"
                )}
              </PressableButton>
              <p className="text-center text-[11px] text-zinc-500">
                Pago seguro · si ya pagaste, tocá Verificar
              </p>
            </div>
          ) : null}

          {/* Info card */}
          <div className="mb-4 rounded-3xl border border-white/10 bg-white/[0.03] p-4">
            <div className="mb-3 flex items-start gap-2.5">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-amber-400/15">
                <Clock className="h-4 w-4 text-amber-300" aria-hidden />
              </div>
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-semibold text-zinc-100">
                  {showBuyMembershipCta ? "Después del pago" : "Puede tardar un rato"}
                </p>
                <p className="text-[13px] leading-relaxed text-zinc-400">
                  {showBuyMembershipCta
                    ? "Cuando se confirma el pago te preparamos el link y te avisamos a "
                    : "Te mandamos el acceso a "}
                  <span className="break-all font-medium text-zinc-200">{email}</span>
                  {showBuyMembershipCta ? "." : "."}
                </p>
              </div>
            </div>
            <div className="border-t border-white/5 pt-3">
              <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                Qué sigue
              </p>
              <StepList items={steps} />
            </div>
          </div>

          <MembershipPortalHint className="mb-2" />

          <p className="pb-2 text-center text-[11px] text-zinc-600">
            Se actualiza sola cada {POLL_INTERVAL_MS / 1000}s ·{" "}
            <a
              href={`mailto:${BRAND_SUPPORT_EMAIL}`}
              className="text-zinc-400 underline-offset-2 hover:text-zinc-200 hover:underline"
            >
              Contacto
            </a>
          </p>
        </div>
      </div>

      {/* Sticky bottom actions — PWA chrome */}
      <div
        className={cn(
          "shrink-0 border-t border-white/10 bg-zinc-950/95 backdrop-blur-xl",
          "px-5 pt-3",
          "pb-[max(0.75rem,env(safe-area-inset-bottom,0px))]",
        )}
      >
        <div className="mx-auto flex w-full max-w-md flex-col gap-2">
          <PressableButton
            className="w-full border-0 bg-white text-zinc-950 hover:bg-zinc-100"
            onClick={() => void checkApproval(true)}
            disabled={manualChecking || polling}
          >
            {manualChecking ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verificando…
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Verificar ahora
              </>
            )}
          </PressableButton>

          <PressableButton
            variant="outline"
            className="w-full border-amber-400/30 bg-amber-400/10 text-amber-50 hover:bg-amber-400/20"
            onClick={() => void notifyAdmin()}
            disabled={notifyDisabled}
          >
            {notifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Avisando…
              </>
            ) : hasNotified && cooldownRemaining > 0 ? (
              <>
                <Clock className="mr-2 h-4 w-4" />
                Esperá {Math.ceil(cooldownRemaining / 60000)} min
              </>
            ) : (
              <>
                <Bell className="mr-2 h-4 w-4" />
                Avisar que estoy esperando
              </>
            )}
          </PressableButton>

          <button
            type="button"
            onClick={() => void signOut()}
            className={cn(
              "flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl",
              "text-[13px] font-medium text-zinc-500",
              "transition-[transform,color,background-color] duration-150 ease-out",
              "active:scale-[0.98] active:bg-white/5 hover:text-zinc-300",
              "touch-manipulation",
            )}
          >
            <LogOut className="h-3.5 w-3.5" />
            Cerrar sesión
          </button>
        </div>
      </div>
    </PendingShell>
  );
}
